import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, DatePicker, Input, message } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { ArrowDownIcon } from 'assets/icons/arrow-down';
import CalendarIcon from 'assets/icons/calendar';
import {
  fields,
  getValidationSchema,
} from 'components/features/claim-registration/form';
import FormActionButtons from 'components/ui/form-action-buttons';
import FormLayoutGroup from 'components/ui/form-layout-group';
import FormSection from 'components/ui/form-section';
import { LazyLoadingSelect } from 'components/ui/lazy-loading-select';
import { SortedSelect } from 'components/ui/sorted-select';
import { EQueryKeys } from 'constants/query-keys';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
  ClaimResponseDto,
  CreateClaimPayloadDto,
  UpdateClaimPayloadDto,
  FilterClaimsQueryDtoStatusesEnum,
} from 'libs/pinnacle-sdk';
import { debounce, isEqual, uniqBy } from 'lodash';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  referenceDataApi,
  userApi,
  catEventApi,
  countryStateCityApi,
  claimsApi,
} from 'services/apis';
import { handleError, validateForm } from 'utils/form';

export interface ClaimTabProps {
  claim: ClaimResponseDto;
  includeNext: boolean;
  includeBack: boolean;
  navigateNext: (step?: number) => void;
  navigateBack: (step?: number) => void;
}

const ClaimRegistration: React.FC<ClaimTabProps> = ({
  claim,
  includeNext,
  includeBack,
  navigateNext,
  navigateBack,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const initialValues = useMemo(
    () => ({
      claimsHandler: claim.claimsHandler,
      dateReported: claim.dateReported ? dayjs(claim.dateReported) : undefined,
      status: claim.status,
      dateOfLoss: claim.dateOfLoss ? dayjs(claim.dateOfLoss) : undefined,
      catCode: claim.catCode,
      causeOfLoss: claim.causeOfLoss,
      currency: claim.currency,
      lossLocation: claim.lossLocation,
      city: claim.city,
      country: claim.country,
      lossDescription: claim.lossDescription,
    }),
    [claim]
  );

  const [form] = Form.useForm<UpdateClaimPayloadDto>();
  const [isFormValid, setIsFormValid] = useState(true);
  const [countryIso, setCountryIso] = useState<string | undefined>(undefined);

  const isEditingMode = useMemo(() => {
    // Define editable statuses - adjust based on your business logic
    const editableStatuses: string[] = [
      FilterClaimsQueryDtoStatusesEnum.Registered,
      FilterClaimsQueryDtoStatusesEnum.InProgress,
    ];
    return editableStatuses.includes(claim.status);
  }, [claim.status]);

  const validateFormFields = useCallback(async () => {
    const values = form.getFieldsValue();
    const isValid = await validateForm(
      values,
      form,
      fields as unknown as (keyof UpdateClaimPayloadDto)[],
      getValidationSchema(t)
    );
    setIsFormValid(isValid);
  }, [form, t]);

  useEffect(() => {
    validateFormFields();
  }, [validateFormFields]);

  const { data: currencies, isLoading: isLoadingCurrencies } = useQuery(
    EQueryKeys.CURRENCIES,
    async () => {
      return referenceDataApi.getCurrencies();
    },
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    }
  );

  const { data: lossCauses, isLoading: isLoadingLossCauses } = useQuery(
    [EQueryKeys.LOSS_CAUSES],
    async () => {
      return referenceDataApi.getLossCauses();
    },
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    }
  );

  const { data: countries, isLoading: isLoadingCountries } = useQuery(
    EQueryKeys.COUNTRIES,
    async () => {
      return countryStateCityApi.getCountries();
    },
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    }
  );

  const { data: cities, isLoading: isLoadingCities } = useQuery(
    [EQueryKeys.CITIES, countryIso],
    async () => {
      if (!countryIso) return;
      return countryStateCityApi.getCitiesByStateAndCountry({
        countryIso,
      });
    },
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    }
  );

  useEffect(() => {
    if (countries && claim.country) {
      const selectedCountry = countries.find(
        (country) => country.name === claim.country
      );
      if (selectedCountry) {
        setCountryIso(selectedCountry.iso);
      }
    }
  }, [countries, claim.country]);

  const updateClaimMutation = useMutation(
    (claimPayload: UpdateClaimPayloadDto) =>
      claimsApi.updateClaim({
        claimId: claim.claimId,
        updateClaimPayloadDto: claimPayload,
      }),
    {
      onSuccess: () => {
        message.success(t('claim.recordUpdatedSuccessfully'));
      },
      onError: async (error: { response: Response }) =>
        handleError({
          error,
          defaultMessage: t('claim.recordUpdatedFailed'),
        }),
    }
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedUpdateClaimMutate = useCallback(
    debounce(
      (
        claimPayload: UpdateClaimPayloadDto,
        onSuccess?: (data: ClaimResponseDto) => void,
        onError?: () => void
      ) => {
        updateClaimMutation.mutate(claimPayload, {
          onSuccess,
          onError,
        });
      },
      500
    ),
    [updateClaimMutation]
  );

  const handleOnFinish = useCallback(() => {
    const fieldsValues = form.getFieldsValue();

    if (isEqual(fieldsValues, initialValues)) {
      return navigateNext();
    }

    return debouncedUpdateClaimMutate(
      {
        ...fieldsValues,
        dateReported: fieldsValues.dateReported
          ? new Date(fieldsValues.dateReported)
          : undefined,
        dateOfLoss: fieldsValues.dateOfLoss
          ? new Date(fieldsValues.dateOfLoss)
          : undefined,
      } as UpdateClaimPayloadDto,
      () => {
        queryClient
          .refetchQueries([EQueryKeys.CLAIM_BY_ID, claim.claimId])
          .then(() => {
            navigateNext();
          });
      }
    );
  }, [
    debouncedUpdateClaimMutate,
    form,
    initialValues,
    navigateNext,
    queryClient,
    claim.claimId,
  ]);

  return (
    <Form
      form={form}
      layout="vertical"
      className="space-y-8"
      initialValues={initialValues}
      onFinish={handleOnFinish}
      onValuesChange={async (changedValues) => {
        if ('country' in changedValues) {
          form.setFieldValue('city', undefined);
          const selectedCountry = countries?.find(
            (country) => country.name === changedValues.country
          );
          setCountryIso(selectedCountry?.iso);
        }
        await validateFormFields();
      }}
    >
      <FormSection title={t('claim.setupDetails')}>
        <FormLayoutGroup mdColumns={2}>
          <Form.Item name="claimsHandler" label={t('claim.claimsHandler')}>
            <LazyLoadingSelect
              fetchOptions={async (page, pageSize, searchText) => {
                const { items } = await userApi.filterUsers({
                  searchText,
                  page,
                  pageSize,
                });
                return {
                  data: items.map((user) => ({
                    label: user.name || '',
                    value: user.userId,
                  })),
                };
              }}
              suffixIcon={<ArrowDownIcon />}
              allowClear
              size="large"
              placeholder={t('claim.selectClaimsHandler')}
              queryKey="claims-handler-lazy-loading-select"
              disabled={!isEditingMode}
            />
          </Form.Item>

          <Form.Item name="dateReported" label={t('claim.dateReported')}>
            <DatePicker
              format={'DD/MM/YYYY'}
              allowClear
              size="large"
              className="w-full"
              suffixIcon={<CalendarIcon />}
              placeholder={t('claim.selectDate')}
              disabled={!isEditingMode}
            />
          </Form.Item>
        </FormLayoutGroup>

        <FormLayoutGroup mdColumns={2}>
          <Form.Item name="dateOfLoss" label={t('claim.dateOfLoss')}>
            <DatePicker
              format={'DD/MM/YYYY'}
              allowClear
              size="large"
              className="w-full"
              suffixIcon={<CalendarIcon />}
              placeholder={t('claim.selectDate')}
              disabled={!isEditingMode}
            />
          </Form.Item>

          <Form.Item name="catCode" label={t('claim.selectCatCode')}>
            <LazyLoadingSelect
              fetchOptions={async (page, pageSize, searchText) => {
                const res = await catEventApi.filterCatEvents({
                  searchText,
                  page,
                  pageSize,
                });
                return {
                  data: (res.items || []).map((cat) => ({
                    label: cat.eventName,
                    value: cat.catEventId,
                  })),
                };
              }}
              suffixIcon={<ArrowDownIcon />}
              allowClear
              size="large"
              placeholder={t('claim.selectCatCode')}
              queryKey="cat-code-lazy-loading-select"
              disabled={!isEditingMode}
            />
          </Form.Item>
        </FormLayoutGroup>

        <FormLayoutGroup mdColumns={2}>
          <Form.Item name="causeOfLoss" label={t('claim.causeOfLoss')}>
            <SortedSelect
              suffixIcon={<ArrowDownIcon />}
              allowClear
              size="large"
              placeholder={t('claim.selectCauseOfLoss')}
              showSearch
              loading={isLoadingLossCauses}
              options={(lossCauses || []).map((lossCause) => ({
                label: lossCause.lossCauseName,
                value: lossCause.lossCauseName,
              }))}
              disabled={!isEditingMode}
            />
          </Form.Item>

          <Form.Item name="currency" label={t('claim.currency')}>
            <SortedSelect
              suffixIcon={<ArrowDownIcon />}
              allowClear
              size="large"
              showSearch
              placeholder={t('claim.currency')}
              loading={isLoadingCurrencies}
              options={(currencies || []).map((currency) => ({
                label: `${currency.name} (${currency.code})`,
                value: currency.code,
              }))}
              disabled={!isEditingMode}
            />
          </Form.Item>
        </FormLayoutGroup>

        <FormLayoutGroup mdColumns={3}>
          <Form.Item name="lossLocation" label={t('claim.lossLocation')}>
            <Input
              size="large"
              placeholder={t('claim.enterLossLocation')}
              disabled={!isEditingMode}
              autoComplete="off"
            />
          </Form.Item>

          <Form.Item name="city" label={t('general.city')}>
            <SortedSelect
              allowClear
              showSearch
              size="large"
              placeholder={t('general.selectField', {
                field: t('general.city'),
              })}
              loading={isLoadingCities}
              disabled={!countryIso || !isEditingMode}
              suffixIcon={<ArrowDownIcon />}
              className="w-full"
              options={uniqBy(cities || [], (city) => city.name).map(
                (city) => ({
                  value: city.name,
                  label: city.name,
                  key: city.id,
                })
              )}
              optionFilterProp="value"
            />
          </Form.Item>

          <Form.Item name="country" label={t('general.country')}>
            <SortedSelect
              allowClear
              showSearch
              size="large"
              loading={isLoadingCountries}
              placeholder={t('general.selectField', {
                field: t('general.country'),
              })}
              suffixIcon={<ArrowDownIcon />}
              className="w-full"
              options={uniqBy(countries || [], (country) => country.name).map(
                (country) => ({
                  value: country.name,
                  label: `${country.emoji} ${country.name}`,
                  key: country.id,
                })
              )}
              optionFilterProp="value"
              disabled={!isEditingMode}
            />
          </Form.Item>
        </FormLayoutGroup>

        <FormLayoutGroup mdColumns={1}>
          <Form.Item name="lossDescription" label={t('claim.lossDescription')}>
            <TextArea
              rows={4}
              placeholder={t('claim.enterCommentHere')}
              disabled={!isEditingMode}
            />
          </Form.Item>
        </FormLayoutGroup>
      </FormSection>

      <FormActionButtons
        confirmText="Next"
        isConfirmDisabled={!isFormValid}
        includeCancel={includeBack}
        includeConfirm={includeNext}
        confirmOutlined={false}
      />
    </Form>
  );
};

export default ClaimRegistration;
